import "./Categories.css";
import React, {memo, useCallback, useState} from "react";
import {DataLoadingStatus, GroupOperation, IndicatorSiblingType} from "../../../Utils/enums";
import {deleteGroup, flattenGroup, updateGroup} from "../../../Utils/queries";
import {DivisionAttribute, DivisionFull, Indicator, IndicatorGroup} from "../../../Utils/response-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronUp,
    faObjectGroup,
    faObjectUngroup,
    faPlusCircle,
    faTrash
} from "@fortawesome/free-solid-svg-icons";
import IndicatorDisplay from "./IndicatorDisplay";

const GroupDisplay = ({group, division_id, attributes_list, setDivisions, status, setStatus, parentCancelAddGroup, onInitiateGroupCreation}: {group: IndicatorGroup, division_id: number, attributes_list: DivisionAttribute[], setDivisions: React.Dispatch<React.SetStateAction<DivisionFull[]>>, status: DataLoadingStatus, setStatus: React.Dispatch<React.SetStateAction<DataLoadingStatus>>, parentCancelAddGroup: (sibling_id: number, sibling_type: IndicatorSiblingType) => void, onInitiateGroupCreation: (sibling_id: number, sibling_type: IndicatorSiblingType) => void}) => {
    const [extended, setExtended] = useState<boolean>(true);
    const [addChildMode, setAddChildMode] = useState<boolean>(group.indicator_group_id === -1);
    const [operation, setOperation] = useState<GroupOperation>(group.bool_operation);
    const stylesSufix = operation === GroupOperation.And? "and" : operation === GroupOperation.Or? "or" : "xor"

    const handleDeleteGroup = useCallback(() => {
        const recursively_process_groups: (igs: IndicatorGroup[]) => IndicatorGroup[] = (igs: IndicatorGroup[]) => {
            return igs.filter(ig => ig.indicator_group_id !== group.indicator_group_id).map((ig) => {return {...ig, child_groups: recursively_process_groups(ig.child_groups)}})
        }

        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to delete this group and all its indicators? This action cannot be undone!");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            let old_div_list: DivisionFull[] = [];
            setDivisions((prev) => {
              old_div_list = prev;
              return prev.map((div) =>
                div.division_id === division_id
                  ? { ...div, categories: div.categories.map(c => c.category_id === group.category_id
                        ? {...c, indicator_groups: recursively_process_groups(c.indicator_groups)}
                        : c
                    ) }
                  : div
              )
            });
            deleteGroup(group.indicator_group_id).then(resp => {
                if (resp.errorCode !== undefined || !resp.success){
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setDivisions(old_div_list);
                    setStatus(DataLoadingStatus.Loaded);
                } else {
                    alert("Group deleted successfully!")
                    setStatus(DataLoadingStatus.NotLoaded);
                }
            });
        }
    }, [group, division_id, setDivisions, setStatus]);

    const handleFlattenGroup = useCallback(() => {
        const recursively_process_group: (ig: IndicatorGroup) => IndicatorGroup = (ig: IndicatorGroup) => {
            const needed_child_group = ig.child_groups.filter(g => g.indicator_group_id === group.indicator_group_id);
            return needed_child_group.length === 0
                ? {...ig, child_groups: ig.child_groups.map(g => recursively_process_group(g))}
                : {...ig, child_groups: [...ig.child_groups.filter(g => g.indicator_group_id !== group.indicator_group_id), ...needed_child_group[0].child_groups], child_indicators: [...ig.child_indicators, ...needed_child_group[0].child_indicators]};
        }

        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to flatten this group?\nAll its indicators will be moved to the parent group.\nThis action cannot be undone!");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            let old_div_list: DivisionFull[] = [];
            setDivisions((prev) => {
              old_div_list = prev;
              return prev.map((div) =>
                div.division_id === division_id
                  ? { ...div, categories: div.categories.map(c => c.category_id === group.category_id
                        ? {...c, indicator_groups: c.indicator_groups.map(ig => recursively_process_group(ig))}
                        : c
                    ) }
                  : div
              );
            });
            flattenGroup(group.indicator_group_id).then(resp => {
                if (resp.errorCode !== undefined || !resp.success){
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setDivisions(old_div_list);
                    setStatus(DataLoadingStatus.Loaded);
                } else {
                    alert("Group flattened successfully!")
                    setStatus(DataLoadingStatus.NotLoaded);
                }
            });
        }
    }, [group, division_id, setDivisions, setStatus]);

    const handleGroupOperationChange = useCallback((newOp: GroupOperation) => {
        const oldOp = operation;
        setStatus(DataLoadingStatus.Loading);
        setOperation(newOp);
        updateGroup(group.indicator_group_id, newOp).then(resp => {
            if (resp.errorCode !== undefined || !resp.success){
                alert(resp.message || `Error: ${resp.errorCode}`);
                setOperation(oldOp);
            }
            setStatus(DataLoadingStatus.Loaded);
        });
    }, [group, operation, setOperation, setStatus]);

    const handleAddChildIndicator = useCallback(() => {
        setAddChildMode(true);
    }, [setAddChildMode]);

    const handleCancelAddChildIndicator = useCallback(() => {
        setAddChildMode(false);
    }, [setAddChildMode]);

    const handleAddChildGroupWithIndicator = useCallback((sibling_id: number, sibling_type: IndicatorSiblingType) => {
        setAddChildMode(false);
        const recursively_process_group: (ig: IndicatorGroup) => IndicatorGroup = (ig: IndicatorGroup) => {
            const needed_child: IndicatorGroup[]|Indicator[] = sibling_type === IndicatorSiblingType.Group ? ig.child_groups.filter(g => g.indicator_group_id === sibling_id) : ig.child_indicators.filter(ind => ind.category_indicator_id === sibling_id);
            return ig.indicator_group_id === group.indicator_group_id
                ? {...ig, child_groups: [...ig.child_groups.filter(g => g.indicator_group_id !== sibling_type || sibling_type !== IndicatorSiblingType.Group), ({indicator_group_id: -1, category_id: group.category_id, parent_group_id: group.indicator_group_id, bool_operation: GroupOperation.And, child_groups: (sibling_type === IndicatorSiblingType.Group? needed_child : []) as IndicatorGroup[], child_indicators: (sibling_type === IndicatorSiblingType.Indicator? needed_child : []) as Indicator[]})], child_indicators: ig.child_indicators.filter(ind => ind.category_indicator_id !== sibling_id || sibling_type !== IndicatorSiblingType.Indicator)}
                : {...ig, child_groups: ig.child_groups.map(g => recursively_process_group(g))};
        }

        setDivisions((prev) => prev.map((div) =>
            div.division_id === division_id
              ? { ...div, categories: div.categories.map(c => c.category_id === group.category_id
                    ? {...c, indicator_groups: c.indicator_groups.map(ig => recursively_process_group(ig))}
                    : c
                ) }
              : div
          )
        );
    }, [group, division_id, setDivisions, setAddChildMode]);

    const handleCancelAddChildGroupWithIndicator = useCallback((sibling_id: number, sibling_type: IndicatorSiblingType) => {
        const recursively_process_group: (ig: IndicatorGroup) => IndicatorGroup = (ig: IndicatorGroup) => {
            const needed_tmp_group = ig.child_groups.filter(g => g.indicator_group_id === -1 && ((sibling_type === IndicatorSiblingType.Indicator && g.child_indicators.some(ind => ind.category_indicator_id === sibling_id)) || (sibling_type === IndicatorSiblingType.Group && g.child_groups.some(ind => ind.indicator_group_id === sibling_id))));
            return ig.indicator_group_id === group.indicator_group_id && needed_tmp_group.length === 1
                ? {...ig, child_groups: [...ig.child_groups.filter(g => g.indicator_group_id !== -1 || !((sibling_type === IndicatorSiblingType.Indicator && g.child_indicators.some(ind => ind.category_indicator_id === sibling_id)) || (sibling_type === IndicatorSiblingType.Group && g.child_groups.some(ind => ind.indicator_group_id === sibling_id)))), ...needed_tmp_group[0].child_groups].sort((g1, g2) => g1.indicator_group_id - g2.indicator_group_id), child_indicators: [...ig.child_indicators, ...needed_tmp_group[0].child_indicators].sort((i1, i2) => i1.category_indicator_id - i2.category_indicator_id)}
                : {...ig, child_groups: ig.child_groups.map(g => recursively_process_group(g))};
        }

        setDivisions((prev) => prev.map((div) =>
            div.division_id === division_id
              ? { ...div, categories: div.categories.map(c => c.category_id === group.category_id
                    ? {...c, indicator_groups: c.indicator_groups.map(ig => recursively_process_group(ig))}
                    : c
                ) }
              : div
          )
        );
    }, [group, division_id, setDivisions]);

    return (
        <div className={`group-wrapper group-${stylesSufix}-wrapper`}>
            <div className={`group-container-head group-${stylesSufix}-head`} onClick={() => {
                setExtended(!extended)
            }}>
                <p className="group-name">
                    {status !== DataLoadingStatus.Loading? <select value={operation.valueOf()} className={`group-operation-select select-${stylesSufix}` } onClick={(e) => e.stopPropagation()} onChange={(e) => handleGroupOperationChange(Number(e.target.value))}>
                        <option value="1" className="select-and">AND</option>
                        <option value="2" className="select-or">OR</option>
                        <option value="3" className="select-xor">XOR</option>
                    </select> : <div className={`group-operation-selected select-${stylesSufix}`}>{GroupOperation[operation]}</div>}
                    {extended && <>
                        {status !== DataLoadingStatus.Loading && group.indicator_group_id !== -1 && !addChildMode &&
                            <button className={`group-action-button button-${stylesSufix}`} aria-label="Add New Rule" title="Add New Rule"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddChildIndicator()
                                    }}><FontAwesomeIcon icon={faPlusCircle}/></button>}
                        {status !== DataLoadingStatus.Loading && group.indicator_group_id !== -1 && !addChildMode &&
                            <button className={`group-action-button button-${stylesSufix}`} aria-label="Create Subgroup With" title="Create Subgroup With"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onInitiateGroupCreation(group.indicator_group_id, IndicatorSiblingType.Group)
                                    }}><FontAwesomeIcon icon={faObjectGroup}/></button>}
                        {status !== DataLoadingStatus.Loading && group.indicator_group_id !== -1 && !addChildMode && !!group.parent_group_id &&
                            <button className={`group-action-button button-${stylesSufix}`} aria-label="Flatten Group" title="Flatten Group"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleFlattenGroup()
                                    }}><FontAwesomeIcon icon={faObjectUngroup}/></button>}
                        {status !== DataLoadingStatus.Loading && group.indicator_group_id !== -1 &&
                            <button className={`group-action-button button-${stylesSufix}`} aria-label="Delete Group" title="Delete Group"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteGroup()
                                    }}><FontAwesomeIcon icon={faTrash}/></button>}
                    </>}
                </p>
                <div className="chevron-wrapper">
                    <FontAwesomeIcon icon={extended ? faChevronUp : faChevronDown}/>
                </div>
            </div>
            <div className="extendable-group-container" style={{display: extended ? "flex" : "none"}}>
                {group.child_groups.map(ig => <GroupDisplay key={ig.indicator_group_id} group={ig}
                                                                  division_id={division_id}
                                                                  attributes_list={attributes_list}
                                                                  setDivisions={setDivisions} status={status} setStatus={setStatus} parentCancelAddGroup={handleCancelAddChildGroupWithIndicator} onInitiateGroupCreation={handleAddChildGroupWithIndicator} />)}
                <table className="indicators-table"><colgroup><col style={{width: "30%"}}/><col style={{width: "10%"}}/><col style={{width: "45%"}}/><col style={{width: "15%"}}/></colgroup>
                    <tbody>
                    {group.child_indicators.map(ind => <IndicatorDisplay key={ind.category_indicator_id} indicator={ind}
                                                                         division_id={division_id}
                                                                         category_id={group.category_id}
                                                                         attributes_list={attributes_list}
                                                                         setDivisions={setDivisions} status={status}
                                                                         setStatus={setStatus}
                                                                         onCancelCreate={handleCancelAddChildIndicator}
                                                                         onCancelCreateGroup={parentCancelAddGroup}
                                                                         onSuccessfulCreate={handleCancelAddChildIndicator}
                                                                         onInitiateGroupCreation={handleAddChildGroupWithIndicator}/>)}
                    {addChildMode && <IndicatorDisplay
                        key={addChildMode.toString()}
                        sibling_id={group.child_indicators.length > 0 ? group.child_indicators[0].category_indicator_id : group.child_groups.length > 0 ? group.child_groups[0].indicator_group_id : 0}
                        sibling_type={group.child_indicators.length > 0 ? IndicatorSiblingType.Indicator : group.child_groups.length > 0 ? IndicatorSiblingType.Group : IndicatorSiblingType.NoSibling}
                        create_group={group.indicator_group_id === -1}
                        group_type={operation}
                        division_id={division_id}
                        category_id={group.category_id}
                        attributes_list={attributes_list}
                        setDivisions={setDivisions}
                        status={status}
                        setStatus={setStatus}
                        onCancelCreate={handleCancelAddChildIndicator}
                        onCancelCreateGroup={parentCancelAddGroup}
                        onSuccessfulCreate={handleCancelAddChildIndicator}
                        onInitiateGroupCreation={handleAddChildGroupWithIndicator}/>}
                    </tbody>
                </table>
            </div>
        </div>
);
};


export default memo(GroupDisplay);